import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { useDialogs, useToasts } from '../../../hooks';
import { useUrlModals } from '../../../utils/useUrlModals';
import { removeSite, siteHeader } from '../../../utils/api/site';
import { redirectToSiteEdit, redirectToSites } from '../../../utils/navigation';
import EditDeleteButtons from '../../EditDeleteButtons/EditDeleteButtons';
import { accessToDeleteBtn } from '../../../utils/accessToDeleteBtn';
import SiteInfo from './SiteInfo';
import SiteTagsWrapper from './SiteTagsWrapper';
import SiteStatus from './SiteStatus';
import SiteMap from '../SiteMap/SiteMap';
import SiteImage from '../SiteImage/SiteImage'; // need to come back
import SitePhotoPreviewModal from '../SiteImage/SitePhotoPreviewModal/SitePhotoPreviewModal';
import { useLocalStorage } from '../../../hooks/useLocalStorage'; // import for useLocalStorage hook
import SitePhotoModal from '../SiteImage/SitePhotoEditModal/SitePhotoEditModal';

const MODAL_CONFIG = {
  previewImages: { isIdParam: false },
  editImages: { isIdParam: false },
};

const SiteDetailsPage = ({
  siteData,
  setSiteData,
  organizationTags,
  values,
  modelFormItems,
  isManager,
}) => {
  // Hook Toast
  const { showToast } = useToasts();
  const [currentSiteImages, setCurrentSiteImages] = useState([]);
  const [siteImages, setSiteImages] = useState([]);
  const [selectedSiteImageId, setSelectedSiteImageId] = useState(null);
  const { openDialog, closeDialog } = useDialogs();
  const { setToLocalStorage } = useLocalStorage();
  const { modals, openModal, closeModal } = useUrlModals(MODAL_CONFIG);

  const hasImages = siteImages?.length > 0;

  useEffect(() => {
    setSiteImages(siteData.site_images || []);
    setCurrentSiteImages(siteData.site_images || []);
  }, [siteData]);

  const onEditClicked = (e) => {
    e.preventDefault();
    setToLocalStorage('siteForEdit', siteData);
    redirectToSiteEdit(siteData.id);
  };
  const onDeleteSite = (s) => {
    const formData = {
      organization_id: s.organization_id,
      sites: [{ id: s.id }],
    };
    removeSite(formData, s.id)
      .then((res) => res.json())
      .then((response) => {
        if (!response.success) {
          throw new Error(response.error);
        }
        closeDialog();
        showToast({ message: 'The site has been deleted successfully', type: 'success' });
        // This allows us redirect the user to the site index page after deleting a site
        redirectToSites();
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
        closeDialog();
      });
  };

  const handleOpenDeleteModal = () => {
    openDialog({
      message: [
        <p className="text-center leading-snug">
          Are you sure you want to delete <b>{siteData.name}</b>? All associated notes and visits will also be deleted.
        </p>,
      ],
      show: true,
      actions: [
        {
          label: 'Cancel',
          variant: 'secondary',
          onClick: closeDialog,
        },
        {
          label: 'Delete site',
          variant: 'primary',
          onClick: () => {
            onDeleteSite(siteData);
            closeDialog();
          },
        },
      ],
    });
  };

  const actionItemButtons = [
    { label: 'Edit', onClick: (e) => onEditClicked(e) },
    { label: 'Delete', onClick: () => handleOpenDeleteModal(siteData) },
  ];
  const actionItems = accessToDeleteBtn(actionItemButtons, isManager);

  // ---Functions and states for SitePhotoModal component - should be added for Edit Sites page ----

  const handleOpenImagePreviewModal = () => {
    openModal('previewImages');
  };

  const handleCloseImagePreviewModal = (params) => {
    closeModal('previewImages');
    if (params.shouldOpenEditModal) {
      openModal('editImages');
    }
  };
  const handleCloseEditImageModal = () => {
    closeModal('editImages');
  };

  if (!siteData.name) return null; // to not load the page until siteData exists

  return (
    <div className="flex flex-col w-85">
      <div className="flex justify-between w-85">
        <div className="w-3/5 pr-10">
          {siteHeader(siteData)}
          <div className="flex flex-col xl2:flex-row mb-8 gap-6">
            {hasImages && (
              <div className="w-full xl2:w-2/3 radius-10px">
                <button type="button" onClick={handleOpenImagePreviewModal} className="w-full">
                  <SiteImage
                    images={siteImages}
                    siteId={siteData.id}
                    organizationId={siteData.organization_id}
                    heightStyle="h-[300px]"
                    isModal={false}
                    onCurrentImageChange={setSelectedSiteImageId}
                  />
                </button>
                {hasImages && (
                  <>
                    {/* Preview Modal */}
                    <SitePhotoPreviewModal
                      currentSiteImages={siteImages}
                      siteId={siteData.id}
                      organizationId={siteData.organization_id}
                      isShow={modals.previewImages?.isOpen || false}
                      onClose={handleCloseImagePreviewModal}
                      isModal
                      initialImageId={selectedSiteImageId}
                    />
                    {/* Edit Modal */}
                    <SitePhotoModal
                      siteId={siteData.id}
                      organizationId={siteData.organization_id}
                      isShow={modals.editImages?.isOpen || false}
                      onClose={handleCloseEditImageModal}
                      updateCarouselImages={setSiteImages}
                      currentSiteImages={currentSiteImages}
                      setCurrentSiteImages={setCurrentSiteImages}
                    />
                  </>
                )}
              </div>
            )}
            <div className={`w-full ${hasImages && 'xl2:w-1/3'} rounded-10px`}>
              <SiteMap site={siteData} setSiteData={setSiteData} icon={siteData.icon} isSmallMap={hasImages} />
            </div>
          </div>

          <SiteStatus site={siteData} />

          <SiteTagsWrapper
            site={siteData}
            organizationTags={organizationTags}
            setSiteData={setSiteData} // to handle change tags for siteData state
          />
        </div>
        <div className="w-2/5  pl-10">
          <div className="flex justify-end pt-3.5 pb-11.25">
            <EditDeleteButtons items={actionItems} />
          </div>
          <SiteInfo
            site={siteData}
            values={values}
            modelFormItems={modelFormItems}
          />
        </div>
      </div>
    </div>
  );
};

SiteDetailsPage.propTypes = {
  siteData: PropTypes.object.isRequired,
  setSiteData: PropTypes.func.isRequired,
  organizationTags: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  modelFormItems: PropTypes.array.isRequired,
  isManager: PropTypes.bool.isRequired,
};
export default SiteDetailsPage;
